import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { Alert } from 'reactstrap';

import ApiService from '@app/Services/ApiService';
import { SignalREvents } from '@app/AppConstants/SignalR';
import { ApiUrls } from '@app/AppConstants/ApiUrls';
import { MaintenanceModel } from '@app/Models/WebApiModels';
import { Icon } from '@app/Components/Icon';
import { signalRService } from '@app/Services/SignalRService';
import { AzureAuth, UserPermissions } from '@app/Components/AzureAuth';
import Extensions from '@app/Services/Extensions';
import { applyDecorators, EmptyProps } from "@app/Helpers/Decorator";

interface IMaintenanceBlockProps {
    isVisible: boolean;
    title: JSX.Element | string;
    caption: JSX.Element | string;
    alertColor?: string;
}

@observer
export default class MaintenanceBar extends React.Component {
    private _store: MaintenanceBarStore;

    constructor(props: EmptyProps) {
        super(props);
        applyDecorators(this);
        this._store = new MaintenanceBarStore();
    }

    componentWillUnmount(): void {
        this._store.unload();
    }

    render() {
        const { maintenance } = this._store;
        if (!maintenance) return null;
        return (
            <React.Fragment>
                <MaintenanceBlock isVisible={maintenance.isNotifyingMaintenanceMessageOn} title="Warning!" caption={maintenance.notifyingMessage || ''} alertColor="warning" />
                <MaintenanceBlock
                    isVisible={maintenance.isMaintenanceOn}
                    title={
                        <React.Fragment>
                            Maintenance mode is turned ON now <Icon name="cog" className="cog" />
                        </React.Fragment>
                    }
                    caption={maintenance.maintenanceMessage || ''}
                    alertColor="danger"
                />
            </React.Fragment>
        );
    }
}

@observer
class MaintenanceBlock extends React.Component<IMaintenanceBlockProps> {
    private _timer: number | null = null;
    private _delay: number = 5 * 60 * 1000;
    @observable isOpen: boolean = true;

    constructor(props: IMaintenanceBlockProps) {
        super(props);
        applyDecorators(this);
    }

    render() {
        const { caption, title, isVisible, alertColor } = this.props;
        if (!isVisible) return null;
        return (
            <Alert color={alertColor ? alertColor : 'primary'} isOpen={this.isOpen} fade={false} className="maintenance-bar">
                <div className="maintenance-body">
                    <div className="maintenance-title">{title}</div>
                    {caption && <div className="maintenance-caption">{caption}</div>}
                </div>
                <a className="close" onClick={this.onClose}>
                    <Icon name="times" />
                </a>
            </Alert>
        );
    }

    onClose = () => {
        this.isOpen = false;
        this.setupInterval();
    };

    setupInterval() {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
        this._timer = window.setTimeout(() => {
            this.isOpen = true;
        }, this._delay);
    }
}

class MaintenanceBarStore {
    @observable maintenance: MaintenanceModel | null = null;

    @action.bound
    async getData() {
        const { data } = await ApiService.getTypedData<MaintenanceModel>(ApiUrls.MaintenanceUrl);
        this.maintenance = data;
    }

    constructor() {
        applyDecorators(this);
        Extensions.executeTimeout(this.getData, 5000, this);
        signalRService.subscribeToGroup(SignalREvents.updateStatusMaintenance, this.updateData);
        signalRService.subscribeToGroup(SignalREvents.sendMaintenanceNotification, this.getData);
    }

    @action
    updateData = () => {
        if (!AzureAuth.hasAccess(UserPermissions.AdminToolsSafe)) {
            location.reload();
        } else {
            void this.getData();
        }
    };

    @action
    unload() {
        signalRService.unsubscribeFromGroup(SignalREvents.updateStatusMaintenance, this.updateData);
        signalRService.unsubscribeFromGroup(SignalREvents.sendMaintenanceNotification, this.getData);
    }
}
